// LiveMap.tsx
import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps as waitGoogleMapsLoad } from '../../../common/loadGoogleMaps';
import { axiosInstance } from '@/services/axios.service';
import { X4bActivityDto, ActivityHelper } from '@/modules/vehicle/engine/X4bActivityDto';
import { ENDPOINTS } from '@/constants/endpoints';
import { createMarkerOverlayClasses } from './CustomMarkerOverlay';
import { cn } from '@/components/ui/cn';
import { debugLog } from '@/common/DebugHelper';
import { LiveMapProps, useInitialRoute, mainRouteColor, routeStrokeWidth, remainingRouteColor } from './useInitialRoute';
import { initJorneySharing } from './initJorneySharing';
import { GetGoogleTokenResponse } from '@/modules/vehicle/engine/ActivityTypes';
import { getVehicleTypeInfo } from '@/modules/vehicle/engine/VehicleType';

function getRouteColor(tripStatus: string): string {
	if (tripStatus === 'ENROUTE_TO_DROPOFF') return remainingRouteColor;

	return mainRouteColor;
}



const ConciergeLiveMap: React.FC<LiveMapProps> = React.memo((props) => {
	const mapRef = useRef<HTMLDivElement>(null);
	const [map, setMap] = useState<google.maps.Map | undefined>();
	const [locationProvider, setLocationProvider] = useState<any>(null);
	//const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [tripStatus, setTripStatus] = useState<string | null>(null);

	// const selectGeoZone = {
	// 	PolygonGeometries: [
	// 		{ Lat: 14.6350728, Lng: 120.9927488 }
	// 	]
	// }

	// holds latest tripId without triggering re-render
	const latestTripIdRef = useRef<string>(undefined);
	const locationProviderRef = useRef<google.maps.journeySharing.FleetEngineTripLocationProvider | null>(null);
	const mapViewRef = useRef<google.maps.journeySharing.JourneySharingMapView | null>(null);

	const customPickupOverlayRef = useRef<any>(null);
	const customDropoffOverlayRef = useRef<any>(null);
	const customVehicleOverlayRef = useRef<any>(null);
	const remainingTillWaipointPolylineRef = useRef<google.maps.Polyline | null>(null);
	const locationHistoryPolylineRef = useRef<google.maps.Polyline | null>(null);
	const locationHistoryDataRef = useRef<Array<{ Lat?: number; Long?: number; TimeStamp?: string }> | null>(null);
	const markersReplacedForCompletionRef = useRef<boolean>(false);
	const latestOrderUuidRef = useRef<string>(undefined);

	useInitialRoute(props, customPickupOverlayRef, customDropoffOverlayRef, setMap, map);

	let lastHeading: number | null = null;


	// Function to fetch location history
	const fetchLocationHistory = async (): Promise<Array<{ Lat?: number; Long?: number; TimeStamp?: string }> | null> => {
		if (!latestOrderUuidRef.current) {
			debugLog('[LiveMap] Cannot fetch location history: no order UUID available');
			return null;
		}

		try {
			const response = await axiosInstance.post(
				ENDPOINTS['/api/X4bActivity/GetLocationHistory'],
				{ BusinessOrderId: latestOrderUuidRef.current }
			);
			const items = response.data?.Data?.Items || [];
			return items;
		} catch (error) {
			console.error('[LiveMap] Failed to fetch location history:', error);
			return null;
		}
	};

	// give parent a setter it can call later
	useEffect(() => {
		if (!props.bindOrderUuidSetter) return;
		props.bindOrderUuidSetter((orderUuid: string) => {
			latestOrderUuidRef.current = orderUuid;
		});
	}, [props.bindOrderUuidSetter]);
	useEffect(() => {
		if (!props.bindTripIdSetter) return;
		props.bindTripIdSetter((tripId: string) => {
			latestTripIdRef.current = tripId;
			if (locationProviderRef.current) {
				locationProviderRef.current.tripId = tripId;
			}
		});
	}, [props.bindTripIdSetter]);

	// Initialize map
	useEffect(() => {
		const initMap = async () => {
			//setIsLoading(true);
			setError(null);
			try {
				// Load Google Maps script
				// TODO: check that it is loaded only once per site load
				await waitGoogleMapsLoad();

				// Try to load saved map position from localStorage
				const savedMapState = localStorage.getItem('conciergeMapState');
				let initialCenter: google.maps.LatLng;
				let initialZoom: number = 20;

				if (savedMapState) {
					try {
						const { center, zoom } = JSON.parse(savedMapState);
						initialCenter = new google.maps.LatLng(center.lat, center.lng);
						initialZoom = zoom;
					} catch (e) {
						console.warn('Failed to parse saved map state, using defaults', e);
						initialCenter = new google.maps.LatLng(14.6350728, 120.9927488);
					}
				} else {
					// Set default center if no saved state
					initialCenter = new google.maps.LatLng(14.6350728, 120.9927488);
				}

				// Set default or selected geo zone center
				let latLng: google.maps.LatLng = initialCenter;
				// Initialize Fleet Engine location provider
				const fleetLocationProvider = new google.maps.journeySharing
					.FleetEngineTripLocationProvider({
						projectId: 'xpress-366609', // TODO[Phase2]: use from config

						authTokenFetcher: async () => {
							if (!latestOrderUuidRef.current) {
								throw new Error('No order UUID available');
							}
							// Note, on initial map display the GetGoogleFleetToken gets called twice. We cannot optimize that. Because first call is actually to get tripId not the token. Xpress proxy provides both in one api method.
							const token = await axiosInstance.get<GetGoogleTokenResponse>(ENDPOINTS['/api/X4bActivity/GetGoogleFleetToken'], { params: { XpressOrderId: latestOrderUuidRef.current } });

							return { token: token.data.Token, expiresInSeconds: token.data.ExpiresInSeconds }

						},
						pollingIntervalMillis: 10000,
						tripId: ''
					});


				setLocationProvider(fleetLocationProvider);
				locationProviderRef.current = fleetLocationProvider;

				// If a tripId was set before init, apply it now
				if (latestTripIdRef.current) {
					fleetLocationProvider.tripId = latestTripIdRef.current;
				}


				const mapView = initJorneySharing(fleetLocationProvider, mapRef, props.activity?.VehicleType);
				// Store mapView for proper cleanup
				mapViewRef.current = mapView;
				setMap(mapView.map);

				// Listen for updates from Fleet Engine
				fleetLocationProvider.addListener('update', (event: { trip: google.maps.journeySharing.Trip }) => {

					// Update trip status state to control route visibility
					if (event.trip?.status) {
						debugLog(`[LiveMap] Trip status updated to: ${event.trip.status}`);
						setTripStatus(event.trip.status);

					}

					// Check if activity is completed or canceled based on activity status (not Google Fleet trip status)
					// This is important because canceled trips before driver acceptance don't exist in Google Fleet
					const isActivityCompleted = props.activity ? ActivityHelper.isCompleted(props.activity) : false;
					const isActivityCanceled = props.activity ? ActivityHelper.isCanceled(props.activity) : false;
					const shouldShowCompletedMarkers = isActivityCompleted || isActivityCanceled || props.markersAsDots;

					const { CustomMarkerOverlay, CompletedMarkerOverlay, VehicleMarkerOverlay } = createMarkerOverlayClasses();
					// Create custom overlays for pickup and dropoff locations if we have trip data
					if (!customPickupOverlayRef.current && event.trip?.plannedPickupLocation) {
						let loc = event.trip.plannedPickupLocation;
						if (loc.lat && loc.lng) {
							debugLog(`[LiveMap] MARKER UPDATE: Creating pickup marker from trip data at (${loc.lat}, ${loc.lng})`);
							const position = new google.maps.LatLng(loc.lat, loc.lng);
							const overlay = new CustomMarkerOverlay(position, "/images/concierge/live-map/pickup-location.svg", "Pick up", props.pickupLocationName);
							overlay.setMap(mapView.map);
							customPickupOverlayRef.current = overlay;
						}
					}

					if (!customDropoffOverlayRef.current && event.trip?.plannedDropOffLocation) {
						let loc = event.trip.plannedDropOffLocation;
						if (loc.lat && loc.lng) {
							debugLog(`[LiveMap] MARKER UPDATE: Creating dropoff marker from trip data at (${loc.lat}, ${loc.lng})`);
							const position = new google.maps.LatLng(loc.lat, loc.lng);
							const overlay = new CustomMarkerOverlay(position, "/images/concierge/live-map/dropoff-location.svg", "Drop off", props.dropOffLocationName);
							overlay.setMap(mapView.map);
							customDropoffOverlayRef.current = overlay;
						}
					}

					// Create or update custom vehicle marker with rotation (but not when trip is completed or canceled)
					if (event.trip?.latestVehicleLocationUpdate && !shouldShowCompletedMarkers) {
						const vehicleLocation = event.trip.latestVehicleLocationUpdate;
						const location = vehicleLocation.location as any;
						const lat = location?.lat ?? location?.latitude;
						const lng = location?.lng ?? location?.longitude;

						if (lat != null && lng != null) {
							const position = new google.maps.LatLng(lat, lng);

							let heading = vehicleLocation.heading;
							if (heading == null) {
								// No heading in this update (google fleet often does it) → keep previous
								heading = lastHeading ?? 0; // or just return without updating rotation
							} else {
								lastHeading = heading;
							}

							const vehicleTypeInfo = getVehicleTypeInfo(props.activity?.VehicleType);
							const headingCorrection = vehicleTypeInfo?.topViewMapHeadingCorrection || 0;
							const rotation = heading + headingCorrection;
							const iconPath = vehicleTypeInfo?.topViewPath || '/images/top-view/sedan.svg';

							if (!customVehicleOverlayRef.current) {
								// Create new vehicle marker
								debugLog(`[LiveMap] MARKER UPDATE: Creating vehicle marker at (${position.lat()}, ${position.lng()}) with rotation ${rotation}`);
								const vehicleOverlay = new VehicleMarkerOverlay(position, iconPath, rotation);
								vehicleOverlay.setMap(mapView.map);
								customVehicleOverlayRef.current = vehicleOverlay;
							} else {
								// Update existing vehicle marker position and rotation
								customVehicleOverlayRef.current.updatePositionAndRotation(position, rotation);
							}
						}
					}

					// Replace markers with completed versions when trip is completed or canceled
					if (shouldShowCompletedMarkers && !markersReplacedForCompletionRef.current) {
						debugLog(`[LiveMap] MARKER UPDATE: Trip completed or canceled (activity status), replacing markers with completed versions`);
						markersReplacedForCompletionRef.current = true;

						// Replace pickup marker
						if (customPickupOverlayRef.current && event.trip.plannedPickupLocation) {
							// Remove old marker
							debugLog(`[LiveMap] MARKER UPDATE: Removing old pickup marker for completion`);
							customPickupOverlayRef.current.setMap(null);

							// Create completed marker
							const loc = event.trip.plannedPickupLocation;
							if (loc.lat && loc.lng) {
								debugLog(`[LiveMap] MARKER UPDATE: Creating completed pickup marker at (${loc.lat}, ${loc.lng})`);
								const position = new google.maps.LatLng(loc.lat, loc.lng);
								const completedOverlay = new CompletedMarkerOverlay(
									position,
									"/images/concierge/live-map/pickup-completed-location.svg",
									"Pick up",
									props.pickupLocationName
								);
								completedOverlay.setMap(mapView.map);
								customPickupOverlayRef.current = completedOverlay;
							}
						}

						// Replace dropoff marker
						if (customDropoffOverlayRef.current && event.trip.plannedDropOffLocation) {
							// Remove old marker
							debugLog(`[LiveMap] MARKER UPDATE: Removing old dropoff marker for completion`);
							customDropoffOverlayRef.current.setMap(null);

							// Create completed marker
							const loc = event.trip.plannedDropOffLocation;
							if (loc.lat && loc.lng) {
								debugLog(`[LiveMap] MARKER UPDATE: Creating completed dropoff marker at (${loc.lat}, ${loc.lng})`);
								const position = new google.maps.LatLng(loc.lat, loc.lng);
								const completedOverlay = new CompletedMarkerOverlay(
									position,
									"/images/concierge/live-map/dropoff-completed-location.svg",
									"Drop off",
									props.dropOffLocationName
								);
								completedOverlay.setMap(mapView.map);
								customDropoffOverlayRef.current = completedOverlay;
							}
						}

						// Remove vehicle marker when trip is completed or canceled
						if (customVehicleOverlayRef.current) {
							debugLog(`[LiveMap] MARKER UPDATE: Removing vehicle marker for trip completion or cancellation`);
							customVehicleOverlayRef.current.setMap(null);
							customVehicleOverlayRef.current = null;
						}
					}

					// Draw route from vehicle to pickup point using TripWaypoint.path
					if (event.trip) {
						//console.log('Processing vehicle-to-pickup route...');

						// Check trip status - we want to show route only when ENROUTE_TO_PICKUP
						const tripStatus = event.trip.status;
						//console.log('Current trip status for routing:', tripStatus);

						if (tripStatus === 'ENROUTE_TO_PICKUP' || tripStatus === 'ENROUTE_TO_DROPOFF') {
							// Get the first remaining waypoint (should be the pickup point)
							// According to Fleet Engine docs, TripWaypoint.path contains:
							// "The path from the previous stop (or the vehicle's current location,
							//  if this stop is the first in the list of stops) to this stop."
							const remainingWaypoints = event.trip.remainingWaypoints;

							//Beware, the route to pickup updates not as well as the vehicle position. So, if you see wrong route then just create new order and test fresh.
							if (remainingWaypoints && remainingWaypoints.length > 0) {
								const firstWaypoint = remainingWaypoints[0];

								// The path property contains the route from vehicle's current location to this waypoint
								if (firstWaypoint.path && Array.isArray(firstWaypoint.path) && firstWaypoint.path.length > 0) {

									// Remove existing vehicle-to-pickup polyline if it exists
									if (remainingTillWaipointPolylineRef.current) {
										debugLog(`[LiveMap] PATH UPDATE: Removing existing vehicle-to-pickup route before creating new one`);
										remainingTillWaipointPolylineRef.current.setMap(null);
									}

									// Create new polyline for vehicle-to-pickup route
									// The path is already an array of LatLng points
									debugLog(`[LiveMap] PATH UPDATE: Creating vehicle-to-pickup route with ${firstWaypoint.path.length} points`);
									const polyline = new google.maps.Polyline({
										path: firstWaypoint.path,
										strokeColor: getRouteColor(tripStatus), // Blue color for vehicle-to-pickup route
										//strokeOpacity: 1.0,
										strokeWeight: routeStrokeWidth,
										map: mapView.map
									});

									remainingTillWaipointPolylineRef.current = polyline;
									//console.log('Vehicle-to-pickup route drawn successfully');
								} else {
									console.log('No path data available in first waypoint');
								}
							} else {
								console.log('No remaining waypoints available');
							}

						} else {
							// Remove vehicle-to-pickup route if trip status is not ENROUTE_TO_PICKUP
							if (remainingTillWaipointPolylineRef.current) {
								debugLog(`[LiveMap] PATH UPDATE: Removing vehicle-to-pickup route (trip status: ${tripStatus})`);
								remainingTillWaipointPolylineRef.current.setMap(null);
								remainingTillWaipointPolylineRef.current = null;
							}

						}
						// Note, the polyline we have may have not aligned well. Google has api snapToRoads. We may try to use it.
						if (tripStatus === 'ENROUTE_TO_DROPOFF' || tripStatus === 'COMPLETE') {

							debugLog('[LiveMap] Triggering GetLocationHistory from Fleet Engine update');
							fetchLocationHistory().then(locationHistory => {
								locationHistoryDataRef.current = locationHistory;
								if (locationHistoryDataRef.current && locationHistoryDataRef.current.length > 0) {
									// Remove existing location history polyline if it exists
									if (locationHistoryPolylineRef.current) {
										debugLog(`[LiveMap] PATH UPDATE: Removing existing location history polyline`);
										locationHistoryPolylineRef.current.setMap(null);
									}

									// Convert location history points to Google Maps LatLng
									const path = locationHistoryDataRef.current
										.filter(point => point.Lat != null && point.Long != null)
										.map(point => new google.maps.LatLng(point.Lat!, point.Long!));

									if (path.length > 0) {
										debugLog(`[LiveMap] PATH UPDATE: Creating location history polyline with ${path.length} points`);
										const polyline = new google.maps.Polyline({
											path: path,
											strokeColor: mainRouteColor, // Use darker color for taken route
											strokeWeight: routeStrokeWidth,
											map: mapView.map,
											zIndex: 1 // Lower z-index so it renders beneath current route
										});
										locationHistoryPolylineRef.current = polyline;
									}
								}
							});
							// Draw location history if we have data

						} else {
							// Remove location history polyline if not ENROUTE_TO_DROPOFF
							if (locationHistoryPolylineRef.current) {
								debugLog(`[LiveMap] PATH UPDATE: Removing location history polyline (trip status: ${tripStatus})`);
								locationHistoryPolylineRef.current.setMap(null);
								locationHistoryPolylineRef.current = null;
							}
						}
					}
				});
				// Set initial viewport
				mapView.map.setCenter(latLng);
				mapView.map.setZoom(initialZoom);


				// Save map state to localStorage when center or zoom changes
				const saveMapState = debounce(() => {
					const center = mapView.map.getCenter();
					const zoom = mapView.map.getZoom();
					if (center && zoom !== undefined) {
						const mapState = {
							center: {
								lat: center.lat(),
								lng: center.lng()
							},
							zoom: zoom
						};
						localStorage.setItem('conciergeMapState', JSON.stringify(mapState));
					}
				}, 1000); // Debounce for 1 second to avoid excessive writes

				// Wait for map to be idle (initialization complete) before attaching save listeners
				const handleFirstIdle = () => {
					// Map is now fully initialized, start listening for user changes
					mapView.map.addListener('center_changed', saveMapState);
					mapView.map.addListener('zoom_changed', saveMapState);

					// Remove this one-time listener
					google.maps.event.removeListener(idleListener);
				};

				const idleListener = mapView.map.addListener('idle', handleFirstIdle);

			} catch (error) {
				console.error('Failed to initialize map:', error);
				setError(error instanceof Error ? error.message : 'Failed to initialize map');
				//setIsLoading(false);
			}
		};

		if (mapRef.current) {
			initMap();
		}

		return () => {
			// Proper cleanup to stop polling and free resources
			//console.log('Cleaning up ConciergeLiveMap resources');

			// Stop the location provider from making further requests
			if (locationProviderRef.current) {
				locationProviderRef.current.tripId = '';
				// Clear the reference to allow garbage collection
				locationProviderRef.current = null;
			}

			// Clean up polyline references
			//remainingRoutePolylinesRef.current = [];
			//takenRoutePolylinesRef.current = [];

			// Clean up vehicle-to-pickup polyline
			if (remainingTillWaipointPolylineRef.current) {
				remainingTillWaipointPolylineRef.current.setMap(null);
				remainingTillWaipointPolylineRef.current = null;
			}

			// Clean up location history polyline
			if (locationHistoryPolylineRef.current) {
				locationHistoryPolylineRef.current.setMap(null);
				locationHistoryPolylineRef.current = null;
			}

			// Clean up custom overlays
			if (customPickupOverlayRef.current) {
				customPickupOverlayRef.current.setMap(null);
				customPickupOverlayRef.current = null;
			}
			if (customDropoffOverlayRef.current) {
				customDropoffOverlayRef.current.setMap(null);
				customDropoffOverlayRef.current = null;
			}
			if (customVehicleOverlayRef.current) {
				customVehicleOverlayRef.current.setMap(null);
				customVehicleOverlayRef.current = null;
			}

			// Reset completion flag
			markersReplacedForCompletionRef.current = false;

			// Clean up the map view
			if (mapViewRef.current) {
				// Clear the map container to stop any ongoing rendering
				if (mapViewRef.current.element) {
					mapViewRef.current.element.innerHTML = '';
				}
				mapViewRef.current = null;
			}

			// Clear state
			setLocationProvider(null);
			setMap(undefined);
		};
	}, []);

	// Helper function for debouncing
	const debounce = (func: Function, wait: number) => {
		let timeout: NodeJS.Timeout;
		return function executedFunction(...args: any[]) {
			const later = () => {
				clearTimeout(timeout);
				func(...args);
			};
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	};


	if (error) {
		return (
			<div style={{
				width: '100%',
				height: '100%',
				minHeight: '500px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: '#f8f9fa'
			}}>
				<div style={{ color: '#dc3545' }}>Error: {error}</div>
			</div>
		);
	}


	return (
		<>

			<div
				ref={mapRef}
				style={{ width: '100%', height: '100%' }}
				className={cn(
					'w-full',
					props.mapDivClassName,
					!props.mapDivClassName && 'min-h-[500px]',
				)}
				id="map"
			/>
		</>
	);
});

//const ConciergeLiveMap = React.memo(LiveMap);
export { ConciergeLiveMap };
